/**
 * Admin · i18n coverage dashboard.
 *
 * Reads /api/admin/i18n/coverage which serves the dashboard JSON written by
 * the CLI script `frontend/scripts/i18n-coverage.cjs`. Pure read-only view —
 * the script is the source of truth.
 */
import { useEffect, useState } from "react";
import { Languages, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { AdminHeader } from "./_shared";
import { LOCALES } from "../../lib/locales-config";

function CoverageBar({ pct, pass }) {
  const tone = pass ? "bg-[#1F3A2A]" : pct >= 80 ? "bg-[#B58A3F]" : "bg-[#A04545]";
  return (
    <div className="h-2 w-full rounded-full bg-[#E6EFE8] overflow-hidden">
      <div
        className={`h-full ${tone} transition-all`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        data-testid="i18n-coverage-bar"
      />
    </div>
  );
}

function StatusBadge({ pass }) {
  if (pass) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#D1F1D6] px-2.5 py-1 text-xs font-medium text-[#1F3A2A]">
        <CheckCircle2 size={14} aria-hidden />
        Ready
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#F4E5C5]/70 px-2.5 py-1 text-xs font-medium text-[#7A5E26]">
      <AlertCircle size={14} aria-hidden />
      Needs work
    </span>
  );
}

export default function AdminI18nCoverage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/i18n/coverage")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Could not load coverage."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const localeLabel = (code) => LOCALES[code]?.autonym || code;

  return (
    <div className="space-y-7" data-testid="admin-i18n-coverage">
      <AdminHeader
        eyebrow="Languages"
        title="Translation coverage"
        action={
          <span className="text-xs text-[#525252]">
            {data?.generated_at
              ? `Updated ${new Date(data.generated_at).toLocaleString()}`
              : "Not yet generated"}
          </span>
        }
      />

      {loading && (
        <p className="font-serif italic text-[#525252]">Loading…</p>
      )}
      {error && (
        <p className="font-serif italic text-[#A04545]">{error}</p>
      )}

      {data && data.note && (
        <div className="rounded-2xl border hairline bg-[#FAF6EE] p-6 text-sm text-[#525252]">
          <p className="font-medium text-[#171717] mb-1">No coverage report yet</p>
          <p>{data.note}</p>
        </div>
      )}

      {data && !data.note && (
        <>
          <p className="text-sm text-[#525252]">
            Source: <span className="font-mono">{data.source}</span> · Threshold:{" "}
            <span className="font-mono">{data.threshold}%</span>
          </p>

          <div className="grid gap-4">
            {Object.entries(data.locales).map(([code, m]) => (
              <div
                key={code}
                data-testid={`i18n-locale-card-${code}`}
                className="rounded-2xl border hairline bg-white p-6 space-y-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Languages size={20} className="text-[#1F3A2A]" aria-hidden />
                    <div>
                      <div className="font-serif text-xl text-[#171717]">
                        {localeLabel(code)}
                      </div>
                      <div className="text-xs font-mono text-[#525252]">{code}</div>
                    </div>
                  </div>
                  <StatusBadge pass={m.pass} />
                </div>

                <div className="flex items-center gap-3">
                  <CoverageBar pct={m.coverage} pass={m.pass} />
                  <span
                    className="font-mono text-sm text-[#171717] w-14 text-right"
                    data-testid={`i18n-coverage-pct-${code}`}
                  >
                    {m.coverage}%
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <KPI label="Total keys" value={m.total} />
                  <KPI label="Translated" value={m.translated} />
                  <KPI label="Placeholder" value={m.placeholder} warn={m.placeholder > 0} />
                  <KPI label="Missing" value={m.missing} warn={m.missing > 0} />
                </div>

                <button
                  type="button"
                  className="text-xs text-[#1F3A2A] underline decoration-dotted underline-offset-4"
                  onClick={() => setExpanded(expanded === code ? null : code)}
                  data-testid={`i18n-expand-${code}`}
                >
                  {expanded === code ? "Hide" : "Show"} namespace breakdown
                </button>

                {expanded === code && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[#525252] border-b hairline">
                          <th className="py-2 pe-3">Namespace</th>
                          <th className="py-2 pe-3 text-right">Total</th>
                          <th className="py-2 pe-3 text-right">Translated</th>
                          <th className="py-2 pe-3 text-right">Placeholder</th>
                          <th className="py-2 pe-3 text-right">Missing</th>
                          <th className="py-2 text-right">Coverage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(m.namespaces).map(([ns, n]) => (
                          <tr key={ns} className="border-b hairline last:border-0">
                            <td className="py-2 pe-3 font-mono">{ns}</td>
                            <td className="py-2 pe-3 text-right">{n.total}</td>
                            <td className="py-2 pe-3 text-right">{n.translated}</td>
                            <td className="py-2 pe-3 text-right">
                              <span className={n.placeholder > 0 ? "text-[#A04545]" : ""}>
                                {n.placeholder}
                              </span>
                            </td>
                            <td className="py-2 pe-3 text-right">
                              <span className={n.missing > 0 ? "text-[#A04545]" : ""}>
                                {n.missing}
                              </span>
                            </td>
                            <td className="py-2 text-right font-mono">{n.coverage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-2xl border hairline bg-[#FAF6EE] p-6 text-sm text-[#525252]">
            <p className="font-medium text-[#171717] mb-1">CI guard</p>
            <p>
              Build will fail when any Tier-1 locale falls below{" "}
              <span className="font-mono">{data.threshold}%</span> coverage. Refresh this report by running{" "}
              <code className="font-mono text-[#171717]">
                cd frontend &amp;&amp; node scripts/i18n-coverage.cjs
              </code>{" "}
              after every translation change.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, warn }) {
  return (
    <div className="rounded-xl border hairline bg-[#FAF6EE] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</div>
      <div
        className={`font-mono text-lg ${warn ? "text-[#A04545]" : "text-[#171717]"}`}
      >
        {value}
      </div>
    </div>
  );
}
